import { trackGenericEvent } from '@/analytics';
import { Column, ResponsiveContainer, Row } from '@/components';
import { clipboardToast } from '@/constants';
import { useService } from '@/containers/ActionServiceV2/context';
import { TabE } from '@/containers/ActionServiceV2/utils';
import { useAuth } from '@/contexts';
import { useWindowResize } from '@/hooks';
import { faCheckCircle, faCopy } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChevronRight } from 'lucide-react';
import React, { FunctionComponent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const tabs = [
  {
    label: 'Principal',
    value: TabE.MAIN
  },
  {
    label: 'Precios',
    value: TabE.PRICES
  },
  {
    label: 'Contenido',
    value: TabE.CONTENT
  },
  {
    label: 'Disponibilidad',
    value: TabE.AVAILABILITY
  },
  {
    label: 'Opciones avanzadas',
    value: TabE.OPTIONS
  }
];

export const Header: FunctionComponent = () => {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [copied, setCopied] = React.useState(faCopy);
  const { isMobileSize } = useWindowResize();
  const { activeTab, handleActiveTab, serviceSlug, service } = useService();
  const [searchParams, setSearchParams] = useSearchParams();

  const filterTabs = service?.has_own_availability
    ? tabs
    : tabs.filter((tab) => tab.value !== TabE.AVAILABILITY);

  const handleClipboard = () => {
    navigator.clipboard.writeText(`https://agora.red/${session?.vendor?.username}/servicios/${serviceSlug}`);
    setCopied(faCheckCircle);
    clipboardToast();
  };

  const onChangeTab = (tab: TabE) => {
    handleActiveTab(tab);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('section');
    setSearchParams(newParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  React.useEffect(() => {
    if (copied === faCheckCircle) {
      setTimeout(() => {
        setCopied(faCopy);
      }, 2000);
    }
  }, [copied]);

  const copyComponent = (
    <div
      className="text-sm font-medium underline underline-offset-2 cursor-pointer"
      onClick={handleClipboard}
    >
      <span>Compartir</span>
      <FontAwesomeIcon fixedWidth size="sm" icon={copied} className="ml-2" />
    </div>
  );

  return (
    <Row className="bg-[#FAFAFA] w-full border-b border-gray-200 sticky left-0 right-0 top-[48px] z-10">
      <ResponsiveContainer className="w-full mx-auto px-4">
        <Column className="w-full items-center">
          <Row align="center" justify="space-between" className="py-4 w-full self-start">
            <Row align="center" className="gap-2 sm:gap-4 text-xl leading-none sm:text-3xl sm:leading-none">
              <p className="cursor-pointer" onClick={() => navigate('/servicios')}>
                {isMobileSize ? '...' : 'Servicios'}
              </p>
              <ChevronRight size={20} />
              <p>Editar servicio</p>
            </Row>
            <div className="flex items-center gap-4">
              {copyComponent}
              {!isMobileSize && (
                <span
                  onClick={() => navigate('/servicios')}
                  className="w-auto flex gap-4 cursor-pointer items-center justify-center font-medium sm:w-[180px] px-8 rounded-full text-black h-[44px] text-base bg-white border"
                >
                  Volver
                </span>
              )}
            </div>
          </Row>

          <div className="w-full overflow-x-auto no-scrollbar">
            <div className="flex min-w-max">
              {filterTabs.map((tab) => (
                <button
                  key={tab.value}
                  className={`
                    flex-shrink-0 px-4 py-3 text-sm font-medium transition-colors duration-200 border-b-2 whitespace-nowrap
                    ${
                      activeTab === tab.value
                        ? 'border-[#0072ff] text-[#0072ff]'
                        : 'border-transparent text-gray-600 hover:text-gray-800'
                    }
                  `}
                  onClick={() => {
                    trackGenericEvent('Services Tab Clicked', {
                      name: tab.label
                    });
                    onChangeTab(tab.value);
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </Column>
      </ResponsiveContainer>
    </Row>
  );
};
